// Ingreso exprés en Alegra para un abono sin factura (ej. pago duplicado de un cliente) — Iter 354
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { SearchableCombobox } from '../../components/ui/searchable-combobox';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const CLP = (v) => (v || 0).toLocaleString('es-CL');
const CAT_TIPO = { expense: 'Gasto', liability: 'Pasivo', asset: 'Activo', income: 'Ingreso', equity: 'Patrimonio' };

export function IngresoExpressDialog({ open, onClose, mov, tipo = 'ingreso_alegra', clienteInicial = null, onDone }) {
  const [cuentas, setCuentas] = useState([]);
  const [cuentaSel, setCuentaSel] = useState(null);
  const [cliQ, setCliQ] = useState('');
  const [clis, setClis] = useState([]);
  const [cliSel, setCliSel] = useState(null);
  const [nota, setNota] = useState('');
  const [creando, setCreando] = useState(false);
  const duplicado = tipo === 'pago_duplicado_cliente';

  useEffect(() => {
    if (!open) return;
    setCuentaSel(null); setNota(''); setClis([]); setCliQ('');
    setCliSel(clienteInicial || null);
    api.get('/conciliacion-bancaria/cuentas-contables').then((r) => {
      const list = r.data.cuentas || [];
      setCuentas(list);
      if (duplicado) {
        const ant = list.find((c) => /ANTICIPOS? DE CLIENTES|ANTICIPOS? CLIENTES/i.test(c.name));
        if (ant) setCuentaSel(ant.id);
      }
    }).catch(() => toast.error('Error cargando el plan de cuentas'));
  }, [open, clienteInicial, duplicado]);

  const buscar = async (q) => {
    if (!q || q.trim().length < 2) { setClis([]); return; }
    try {
      const r = await api.get('/conciliacion-bancaria/proveedores-alegra', { params: { q: q.trim() } });
      setClis(r.data.proveedores || []);
    } catch { setClis([]); }
  };

  const confirmar = async () => {
    if (!cuentaSel || !cliSel) { toast.error('Selecciona cuenta contable y cliente'); return; }
    if (duplicado && nota.trim().length < 5) { toast.error('Escribe la explicación del pago duplicado'); return; }
    setCreando(true);
    try {
      const r = await api.post(`/conciliacion-bancaria/movimientos/${mov.id}/ingreso-express`, {
        categoria_id: String(cuentaSel), cliente_alegra_id: String(cliSel.alegra_id), tipo, nota,
      });
      toast.success(r.data.message);
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creando el ingreso en Alegra'); }
    finally { setCreando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dialog-ingreso-express">
        <DialogHeader><DialogTitle>{duplicado ? '⚠ Pago duplicado / a favor del cliente → ingreso en Alegra' : '⚡ Ingreso exprés en Alegra'}</DialogTitle></DialogHeader>
        {mov && (
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <p className="font-medium">{mov.detalle}</p>
              <p className="text-xs text-muted-foreground">{fmtFecha(mov.fecha)} · Abono <b className="text-emerald-600">${CLP(mov.abono)}</b>{mov.cuenta ? ` · cta ${mov.cuenta}` : ''}</p>
            </div>
            {duplicado && (
              <p className="flex gap-2 rounded bg-amber-50 p-2 text-xs text-amber-800" data-testid="ingreso-aviso-duplicado">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                El abono quedará conciliado y <b>DESTACADO</b> permanentemente con tu explicación. En Alegra se registrará como ingreso a la cuenta elegida (sugerida: Anticipos de clientes) para que el saldo a favor del cliente quede visible.
              </p>
            )}
            <div>
              <p className="mb-1 text-xs font-medium">Cuenta contable</p>
              <SearchableCombobox items={cuentas.map((c) => ({ value: c.id, label: c.name, subLabel: CAT_TIPO[c.type] || c.type }))}
                value={cuentaSel} onChange={(item) => setCuentaSel(item ? item.value : null)} placeholder="Ej: Anticipos de clientes..." testid="combobox-cuenta-ingreso" />
            </div>
            <div>
              <p className="mb-1 text-xs font-medium">Cliente / contacto Alegra</p>
              <div className="flex gap-1">
                <Input value={cliQ} onChange={(e) => setCliQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && buscar(cliQ)}
                  placeholder="Buscar por nombre o RUT..." className="h-8 text-xs" data-testid="input-cli-ingreso" />
                <Button size="sm" variant="outline" className="h-8" onClick={() => buscar(cliQ)} data-testid="btn-buscar-cli-ingreso">Buscar</Button>
              </div>
              {clis.length > 0 && (
                <div className="mt-1 max-h-36 space-y-1 overflow-y-auto">
                  {clis.map((p) => (
                    <label key={p.alegra_id} className={`flex cursor-pointer items-center gap-2 rounded-lg border p-1.5 text-xs ${cliSel?.alegra_id === p.alegra_id ? 'border-sky-400 bg-sky-50' : ''}`}>
                      <input type="radio" name="cli-ingreso" checked={cliSel?.alegra_id === p.alegra_id} onChange={() => setCliSel(p)} data-testid={`cli-radio-${p.alegra_id}`} />
                      <span className="flex-1 truncate">{p.nombre}</span><span className="text-[10px] text-muted-foreground">{p.identificacion}</span>
                    </label>
                  ))}
                </div>
              )}
              {cliSel && <p className="mt-1 text-xs text-emerald-700" data-testid="cli-ingreso-sel">✓ {cliSel.nombre} {cliSel.identificacion ? `(${cliSel.identificacion})` : ''}</p>}
            </div>
            <Textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2} data-testid="ingreso-nota"
              placeholder={duplicado ? 'Explicación obligatoria (ej: el cliente pagó dos veces la factura 2513; se devolverá / se aplicará a la próxima factura)' : 'Nota (opcional)'} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-sky-600 hover:bg-sky-700" disabled={creando || !cuentaSel || !cliSel} onClick={confirmar} data-testid="btn-confirmar-ingreso-express">
            {creando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />} Crear ingreso y conciliar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
